import React, { useCallback, useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Alert, Box, Button, Chip, FormControlLabel, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import { exportAdminHighlights, exportCoachingFeedback, getCoachingSettings, listAdminAnalysisRuns, saveCoachingSettings } from '../../service'
import { apiErrorText, estimateLabel, plural, rangeText, saveJsonFile } from '../../service/coaching'

const dateLabel = value => {
  const parsed = new Date(value)
  return value && !Number.isNaN(parsed.getTime()) ? parsed.toLocaleString() : '-'
}
const scope = run => `${run.clip ? `Clip ${rangeText(run.start_seconds, run.end_seconds)}` : 'Whole video'}${run.coaching_context ? ' · coaching with profile' : ''}`
const cost = report => estimateLabel(report)
const runLink = run => `/upload/${encodeURIComponent(run.upload_id)}?coaching=${encodeURIComponent(run.id)}`
const viewable = run => run.status === 'complete' || (run.status === 'failed' && run.usage_report)

// Settings shared by the API and the analysis control function (stored in the database).
function CoachingSettings () {
  const [settings, setSettings] = useState(null)
  const [adminOnly, setAdminOnly] = useState(false)
  const [prices, setPrices] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState(null)
  const apply = value => {
    setSettings(value); setAdminOnly(Boolean(value.analysis_admin_only))
    setPrices(value.model_price_table ? JSON.stringify(value.model_price_table, null, 2) : '')
  }
  useEffect(() => {
    getCoachingSettings().then(apply).catch(() => setMessage({ severity: 'warning', text: 'Coaching settings could not be loaded.' }))
  }, [])
  const save = async () => {
    setMessage(null)
    let table = null
    if (prices.trim()) {
      try { table = JSON.parse(prices) } catch (_) { setMessage({ severity: 'error', text: 'The price table is not valid JSON.' }); return }
    }
    setBusy(true)
    try {
      apply(await saveCoachingSettings({ analysis_admin_only: adminOnly, model_price_table: table }))
      setMessage({ severity: 'success', text: 'Saved. New and existing runs use these settings right away; no deploy is needed.' })
    } catch (e) {
      setMessage({ severity: 'error', text: apiErrorText(e, 'Settings were not saved. Retry in a moment.') })
    } finally { setBusy(false) }
  }
  if (!settings) return message ? <Alert severity={message.severity} sx={{ mt: 1.5 }}>{message.text}</Alert> : null
  const status = settings.price_table_status || {}
  return <Box component="section" aria-label="Coaching beta settings" sx={{ mt: 2 }}>
    <Typography variant="subtitle2">Settings</Typography>
    <FormControlLabel sx={{ mt: 0.5 }} control={<Switch checked={adminOnly} disabled={busy} onChange={e => setAdminOnly(e.target.checked)} />}
      label="Limit all AI analysis to admins (not only clip coaching)" />
    <Typography variant="body2" color="text.secondary">Clip coaching is always admin-only during the beta. Turn this on to also stop whole-game, automatic and re-run analysis for other owners, including runs they already queued.</Typography>
    <Typography variant="body2" sx={{ mt: 1.5 }}>
      <strong>Cost estimate:</strong> {status.ready ? `set up${status.model ? ` for ${status.model}` : ''}` : status.problem || 'not set up yet'}
    </Typography>
    <Typography variant="body2" color="text.secondary">Paste the current on-demand prices for the configured model from the Amazon Bedrock pricing page (USD per million tokens), with where and when you read them. Leave empty to show token counts only.</Typography>
    <TextField label="Model price table (JSON)" value={prices} disabled={busy} onChange={e => setPrices(e.target.value)} multiline minRows={4} maxRows={14} fullWidth sx={{ mt: 1 }}
      inputProps={{ spellCheck: false, style: { fontFamily: 'monospace', fontSize: 13 } }} />
    <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
      <Button size="small" disabled={busy} onClick={() => setPrices(JSON.stringify(settings.price_table_template, null, 2))}>Start from the template</Button>
      <Button size="small" variant="contained" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save settings'}</Button>
    </Stack>
    {message && <Alert severity={message.severity} sx={{ mt: 1 }}>{message.text}</Alert>}
  </Box>
}

// Admin-only AI coaching beta (D18): your own run costs, the learning-loop exports and settings.
export default function CoachingBetaPanel () {
  const [runs, setRuns] = useState([])
  const [error, setError] = useState('')
  const [message, setMessage] = useState(null)
  const [busy, setBusy] = useState('')
  const load = useCallback(async () => {
    try { setError(''); setRuns((await listAdminAnalysisRuns({ limit: 20 })).runs || []) } catch (e) { setError('Recent analysis runs could not be loaded.') }
  }, [])
  useEffect(() => { load() }, [load])
  const download = async (kind) => {
    setBusy(kind); setMessage(null)
    try {
      const data = kind === 'highlights' ? await exportAdminHighlights() : await exportCoachingFeedback()
      const date = new Date().toISOString().slice(0, 10)
      saveJsonFile(data, kind === 'highlights' ? `highlights-coaching-corpus-${date}.json` : `coaching-feedback-${date}.json`)
      setMessage({ severity: 'success', text: kind === 'highlights' ? `Downloaded ${data.count} of your highlights.` : `Downloaded ${data.count} coaching ratings.` })
    } catch (e) {
      setMessage({ severity: 'error', text: 'The download failed. Retry in a moment.' })
    } finally { setBusy('') }
  }
  return <Paper component="section" variant="outlined" aria-label="AI coaching beta" sx={{ p: 2 }}>
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h6" component="h2">AI coaching beta</Typography>
        <Typography variant="body2" color="text.secondary">Clip coaching is admin-only while it is a beta. Download your own highlights as coaching material, and your statement ratings as the learning signal. Exports contain no links, tokens or storage locations.</Typography>
      </Box>
      <Button variant="outlined" startIcon={<DownloadIcon />} disabled={Boolean(busy)} onClick={() => download('highlights')}>Download my highlights (JSON)</Button>
      <Button variant="outlined" startIcon={<DownloadIcon />} disabled={Boolean(busy)} onClick={() => download('feedback')}>Download coaching ratings (JSON)</Button>
    </Stack>
    {message && <Alert severity={message.severity} sx={{ mt: 1.5 }}>{message.text}</Alert>}
    {error && <Alert severity="warning" sx={{ mt: 1.5 }}>{error}</Alert>}
    <Typography variant="subtitle2" sx={{ mt: 2 }}>Your recent analysis runs</Typography>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 13 }}>Model cost is estimated from recorded tokens and the saved price table; worker compute is not included. Your AWS bill is authoritative.</Typography>
    <TableContainer sx={{ mt: 1, display: { xs: 'none', md: 'block' } }}>
      <Table size="small" aria-label="Recent analysis runs">
        <TableHead><TableRow>
          <TableCell>Started</TableCell><TableCell>Video and player</TableCell><TableCell>Scope</TableCell>
          <TableCell align="right">Model calls</TableCell><TableCell align="right">Tokens in / out</TableCell><TableCell align="right">Model cost</TableCell><TableCell />
        </TableRow></TableHead>
        <TableBody>
          {runs.map(run => {
            const report = run.usage_report
            return <TableRow key={run.id}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateLabel(run.created_at)}</TableCell>
              <TableCell>{run.upload_name || 'Video'} · {run.player_name || 'Player'} <Chip size="small" variant="outlined" label={run.status} /></TableCell>
              <TableCell>{scope(run)}</TableCell>
              <TableCell align="right">{report ? report.totals.calls : '-'}</TableCell>
              <TableCell align="right">{report ? `${report.totals.input_tokens.toLocaleString('en-US')} / ${report.totals.output_tokens.toLocaleString('en-US')}` : '-'}</TableCell>
              <TableCell align="right">{cost(report)}</TableCell>
              <TableCell>{viewable(run) && <Button size="small" component={RouterLink} to={runLink(run)}>{run.clip ? 'View coaching' : 'View report'}</Button>}</TableCell>
            </TableRow>
          })}
          {!runs.length && <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No analysis runs yet.</Typography></TableCell></TableRow>}
        </TableBody>
      </Table>
    </TableContainer>
    <Stack spacing={1} sx={{ mt: 1, display: { xs: 'flex', md: 'none' } }} role="list" aria-label="Recent analysis runs (compact)">
      {runs.map(run => <Box key={run.id} role="listitem" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{run.upload_name || 'Video'} · {run.player_name || 'Player'}</Typography>
        <Typography variant="body2">Model cost: {cost(run.usage_report)}</Typography>
        <Typography variant="body2" color="text.secondary">{scope(run)} · {run.status} · {dateLabel(run.created_at)}</Typography>
        {run.usage_report && <Typography variant="body2" color="text.secondary">{plural(run.usage_report.totals.calls, 'call')} · {run.usage_report.totals.input_tokens.toLocaleString('en-US')} in / {run.usage_report.totals.output_tokens.toLocaleString('en-US')} out tokens</Typography>}
        {viewable(run) && <Button size="small" component={RouterLink} to={runLink(run)} sx={{ mt: 0.5, px: 0 }}>{run.clip ? 'View coaching' : 'View report'}</Button>}
      </Box>)}
      {!runs.length && <Typography variant="body2" color="text.secondary">No analysis runs yet.</Typography>}
    </Stack>
    <CoachingSettings />
  </Paper>
}
